import { JwtPayload, jwtDecode } from "jwt-decode";
import { saveItem, getItem, removeItem } from "./session_storage_utils";
import { refreshSSOToken } from "./rest_utils";
import { logout } from "../commands/commands";

export const SSO_USER_ABORT = "<SSO_USER_ABORT>";
export const SSO_TOKEN_NAME = "sso_token";
export const SSO_REFRESH_TOKEN_NAME = "sso_refresh_token";

export async function openSSOLoginDialog(
  loginUrl: string,
  width?: number,
  height?: number
): Promise<string> {
  return new Promise((resolve, reject) => {
    try {
      Office.context.ui.displayDialogAsync(
        loginUrl,
        // displayInIframe important for Outlook Web
        { width: width ?? 50, height: height ?? 66, displayInIframe: true },
        (asyncResult) => {
          if (asyncResult.status === Office.AsyncResultStatus.Failed) {
            reject(new Error(`Dialog could not be opened: ${asyncResult.error.message}`));
            return;
          }

          const dialog = asyncResult.value;

          // Event-Handler: message received
          dialog.addEventHandler(Office.EventType.DialogMessageReceived, (arg) => {
            if ("message" in arg) {
              dialog.close();
              resolve(arg.message); // Token oder Auth-Code vom Login-Dialog
            } else {
              reject(new Error("Keine Nachricht im Dialog-Event."));
            }
          });

          // Optional: cancel or error event
          dialog.addEventHandler(Office.EventType.DialogEventReceived, (event) => {
            console.warn("Dialog Event:", event);
            if ("error" in event) {
              switch (event.error) {
                case 12002:
                  reject(new Error("SSO-Webseite ist nicht erreichbar oder falsch konfiguriert."));
                  break;
                case 12003:
                  reject(new Error("HTTP Protokoll ist nicht erlaubt. Bitte HTTPS verwenden."));
                  break;
                case 12006:
                  // reject(new Error("Der SSO-Dialog wurde vom Benutzer geschlossen."));
                  resolve(SSO_USER_ABORT);
                  break;
                default:
                  reject(new Error(`Fehler im SSO-Dialog: ${event.error}`));
              }
            } else {
              reject(new Error("Unerwarteter Fehler beim Schließen des SSO Dialog Fensters."));
            }
            dialog.close();
          });
        }
      );
    } catch (error) {
      reject(error);
    }
  });
}

export class SSOTokenPair {
  token: SSOToken;
  refreshToken: SSOToken;
  private refreshTimeoutId: number | null = null;

  constructor(tokenStr: string, refreshTokenStr: string, login?: boolean) {
    this.token = new SSOToken(SSO_TOKEN_NAME, tokenStr);
    this.refreshToken = new SSOToken(SSO_REFRESH_TOKEN_NAME, refreshTokenStr);

    if (this.token.isExpired()) {
      console.warn("SSO Token is already expired!");

      if (this.refreshToken.isExpired()) {
        console.warn("SSO Refresh Token is also expired!");

        // re-authentication required
        logout();
      }
    }

    // setTimeout(async () => {
    //   try {
    //     console.log("Refreshing SSO Token...");
    //     const newTokens = await refreshSSOToken(this.refreshToken.token);
    //     console.log("old token", this.token.token);
    //     this.token = new SSOToken(SSO_TOKEN_NAME, newTokens.access_token);
    //     console.log("new token", this.token.token);
    //     this.refreshToken = new SSOToken(SSO_REFRESH_TOKEN_NAME, newTokens.refresh_token);
    //     console.log("SSO Token successfully refreshed.");
    //   } catch (error) {
    //     console.error(error);
    //     logout();
    //   }
    // }, 20_000);

    this.scheduleTokenRefresh();

    if (login) {
      window.location.href = "menu.html";
    }
  }
  private scheduleTokenRefresh(): void {
    // Clear any existing timeout
    if (this.refreshTimeoutId !== null) {
      clearTimeout(this.refreshTimeoutId);
    }

    // Calculate when to refresh: 80% of token lifetime or 5 minutes before expiry
    const expiresInMs = this.token.expiresInMilliseconds();
    const tokenLifetimeMs = this.token.expiresAt().getTime() - this.token.issuedAt().getTime();

    const refreshBeforeExpiryMs = Math.min(
      tokenLifetimeMs * 0.2, // 20% before expiry
      1 * 60 * 1000 // 1 minute
    );

    const refreshInMs = Math.max(
      expiresInMs - refreshBeforeExpiryMs,
      30_000 // Minimum 30 seconds from now
    );

    console.log(`Token refresh scheduled in ${Math.round(refreshInMs / 1000)} seconds`);

    this.refreshTimeoutId = window.setTimeout(async () => {
      await this.performTokenRefresh();
    }, refreshInMs);
  }

  private async performTokenRefresh(): Promise<void> {
    try {
      console.log("Refreshing SSO Token...");

      // Check if refresh token is still valid
      if (this.refreshToken.isExpired()) {
        console.error("Refresh token has expired. Re-authentication required.");
        logout();
        return;
      }

      const newTokens = await refreshSSOToken(this.refreshToken.token);

      console.log("SSO Token successfully refreshed.");

      // Update tokens
      this.token = new SSOToken(SSO_TOKEN_NAME, newTokens.access_token);
      this.refreshToken = new SSOToken(SSO_REFRESH_TOKEN_NAME, newTokens.refresh_token);

      // Schedule next refresh
      this.scheduleTokenRefresh();
    } catch (error) {
      console.error("Failed to refresh SSO token:", error);
      logout();
    }
  }

  public cancelRefresh(): void {
    if (this.refreshTimeoutId !== null) {
      clearTimeout(this.refreshTimeoutId);
      this.refreshTimeoutId = null;
    }
  }

  public async manualRefresh(): Promise<void> {
    this.cancelRefresh();
    await this.performTokenRefresh();
  }
}

class SSOToken {
  token: string;
  name: string;
  decodedToken: JwtPayload;

  constructor(name: string, token: string) {
    this.token = token;
    this.name = name;

    this.decodedToken = jwtDecode(this.token);

    // if (!this.decodedToken.exp || !this.decodedToken.iat) {
    //   throw new Error("Invalid JWT token: missing 'exp' or 'iat' claims.");
    // }

    // console.log(name.toUpperCase() + " - issue date: ", this.issuedAt().toLocaleString());
    // console.log(name.toUpperCase() + " - expiration date: ", this.expiresAt().toLocaleString());
    // console.log(name.toUpperCase() + " - expires in ms: ", this.expiresInMilliseconds());
    // console.log(name.toUpperCase() + " - expired: ", this.isExpired());

    this.saveToStorage();
  }

  public issuedAt(): Date {
    return new Date(this.decodedToken.iat! * 1000);
  }

  public expiresAt(): Date {
    return new Date(this.decodedToken.exp! * 1000);
  }

  public expiresInMilliseconds(): number {
    return this.decodedToken.exp! * 1000 - Date.now();
  }

  public isExpired(): boolean {
    // 10 seconds buffer
    return Date.now() >= this.decodedToken.exp! * 1000 + 10_000;
  }

  public saveToStorage(): void {
    // saveItem(this.name, this.token);
    localStorage.setItem(this.name, this.token);
  }

  public removeFromStorage(): void {
    // removeItem(this.name);
    localStorage.removeItem(this.name);
  }
}

export async function loadSSOTokens(): Promise<void> {
  try {
    const normalLoginToken = localStorage.getItem("authToken");
    if (normalLoginToken) {
      console.log("Normal login token found, skipping SSO token load.");
      return;
    }
    console.log("No normal login token found, attempting to load SSO tokens.");

    // const sso_token = getItem<string>(SSO_TOKEN_NAME);
    // const sso_refresh_token = getItem<string>(SSO_REFRESH_TOKEN_NAME);
    const sso_token = localStorage.getItem(SSO_TOKEN_NAME);
    const sso_refresh_token = localStorage.getItem(SSO_REFRESH_TOKEN_NAME);

    if (!sso_token || !sso_refresh_token) {
      console.log("No SSO tokens found in storage.");
      return;
    }

    const _ = new SSOTokenPair(sso_token, sso_refresh_token);
  } catch (error) {
    console.error("Error loading SSO tokens:", error);
  }
}
